﻿import type { Transcript } from 'components/Transcript/Transcript';
import { ApiCaller, FullfilledApiResponse } from 'apis/ApiCaller';
import type { StudentGroup } from 'apis/StudentGroupApi';
import { PaginatedResult } from 'apis/TransactionApi';
import { getFormattedDateApi } from 'utils';
import { Country } from 'apis/CountryApi';
import { State } from 'apis/StateApi';

/** Valid student-panel environment targets for {@link StudentApi.GetStudentLoginBridgeUrl}. */
export type StudentLoginTarget =
    | 'current'
    | 'prod'
    | 'stage'
    | 'uat'
    | 'dev'
    | 'localhost';

/** Valid sort column names for Student GetAllPaged (OtherRoles context). */
export type StudentOtherRolesSortField =
    | 'Id'
    | 'FirstName'
    | 'LastName'
    | 'EmailAddress'
    | 'UserName'
    | 'LoginId'
    | 'HasAccommodations'
    | 'StudentNumber'
    | 'LastAccessDate'
    | 'Active'
    | 'GroupCodesString'
    | 'AccessCodesString';

/** Valid sort column names for Student GetAllPaged (TestingCenter context). */
export type StudentTestingCenterSortField =
    | 'Id'
    | 'FirstName'
    | 'LastName'
    | 'EmailAddress'
    | 'UserName'
    | 'HasAccommodations'
    | 'StudentNumber'
    | 'LastAccessDate'
    | 'DateCreated'
    | 'Groups'
    | 'archived'
    | 'GroupCodesString'
    | 'AccessCodesString';

/** Valid sort column names for Student GetAllPaged (System/SuperAdmin context). */
export type StudentSystemSortField =
    | 'Id'
    | 'FirstName'
    | 'LastName'
    | 'EmailAddress'
    | 'UserName'
    | 'LoginId'
    | 'HasAccommodations'
    | 'StudentNumber'
    | 'LastAccessDate'
    | 'Active'
    | 'GroupCodesString'
    | 'AccessCodesString';

/** Union of all Student GetAllPaged sort field types. */
export type StudentSortField = StudentOtherRolesSortField | StudentTestingCenterSortField | StudentSystemSortField;

export type CurrentUserPartners = {
    Name: string;
    PartnerId: number;
    Type: number;
};

export interface Certificate {
    CertificateContent: string;
    CertificateUrl: string;
    ClassroomAccessCode: string;
    Course: string;
    CourseStudentId: number;
    Id: number;
    IssuedOn: Date;
    IssuedVia: string;
    StudentName: string;
    Vendor: string;
}

export interface SectionTestLogs {
    Comment: string;
    SectionTestResultId: number;
    UpdatedAt: string;
    UserThatUpdated: string;
}

export interface TestSectionDetail {
    Description: string;
    DisplayOrder: number;
    Id: number;
    LockoutDate: string;
    NumQuestions: number;
    ParentDueDate: string;
    ParentLastModified: string;
    ParentStartDate: string;
    SectionName: string;
    SectionResultId: number;
    SectionTestLogs: SectionTestLogs[];
    SectionTestResultId: number;
    StartDate: string;
    Status: string;
    StatusId: number;
    TestId: number;
    TestName: string;
    TimeLimitMin: number;
}

export interface NewStudent extends Student {
  Country: Country;
  Password: string;
  Role: 'Student' | 'Admin';
  SelectedPartner: CurrentUserPartners;
  State: State;
  StudentNumber: string;
}

export interface Student {
  AccessCodesString?: string;
  Archived?: boolean;
  CustomFields?: CustomFields;
  DateCreated: any;
  Email: string;
  Firstname: string;
  Fullname: string;
  GroupCodesString?: string;
  Groups?: StudentGroup[];
  GroupsString?: string;
  Id: number;
  IsAda: boolean;
  LastAccessDate?: string;
  LastAccessDateString?: string;
  Lastname: string;
  LoginUserID?: string;
  Name?: string;
  PartnerType: number;
  StudentNumber: string;
  Username: string;
}

export interface CustomFields {
  [FieldName: string]: string;
}

/**
 * A personally identifiable information (PII) claim that has been set for a student.
 * Mirrors GMetrix.Data.Entities.RequiredClaim.
 */
export interface RequiredClaim {
  ClaimType: string;
  Display: number;
  Name: string;
  Value: string;
}

/**
 * A single course/exam result for a student, including pass/fail status and a
 * certificate link when a certificate is available.
 */
export interface StudentCourseResult {
  CertificateUrl: string | null;
  CourseName: string;
  CourseStudentId: number;
  DateCompleted: string | null;
  Passed: boolean | null;
  Score: number | null;
  Status: string;
}

export interface StudentInfoField {
  CreateDate: Date;
  CreatedBy: number;
  FieldName: string;
  PartnerId: number;
}

export interface StudentWithDetails extends Student {
  Active: boolean;
  Country?: Country;
  FeatureRingId: number;
  RegistrationDate?: string;
  RegistrationDateString?: string;
  State?: State;
}

/**
 * A student's account recovery secret question and answer.
 * Sensitive — only populated for system administrators.
 */
export type StudentSecretQuestion = {
  Answer: string | null;
  Question: string | null;
};

/**
 * A product that can be associated with a student's access code redemption.
 * Mirrors GMetrix.Data.Entities.Product.
 */
export type StudentAccessProduct = {
    Id: number;
    Name: string;
};

/**
 * Details of a single student's redemption of an access code (StudentContentAccess record).
 * Mirrors GMetrix.Data.Entities.StudentContentAccess.
 */
export type StudentContentAccessDetail = {
    AdminNotes: string;
    BooksRemaining: number | null;
    CloudAccessRemaining: number | null;
    CoursesRemaining: number | null;
    CreatedBy: string;
    DateRedeemed: string;
    DisplayName: string;
    ExpirationDate: string;
    IsActive: boolean;
    IsAssociateProduct: number | null;
    LastModified: string;
    NumFinishedTests: number | null;
    NumSavedTests: number | null;
    ProductID: number | null;
    ProductName: string;
    Products: StudentAccessProduct[];
    StudentAccessId: number;
    TestingCenter: string;
    TestsRemaining: number | null;
};

/**
 * Represents an api handler for the StudentsController.
 * */
export class StudentApi {

    private static apiCaller = new ApiCaller('Students');

    /**
   * Create a new user whether it be an Admin or a Student and return Success/Fail, if Successful it will return profileId of new user.
   * @param Firstname The  first name of user.
   * @param Lastname The last name of user.
   * @param Username The users username used to login.
   * @param Email Optional: (If sending password reset email it's required) The users email.
   * @param Password Optional: (If sending password reset email it's not required) The password of the user.
   * @param StudentNumber Optional: The student number of the student.
   * @param Groups Optional: The classes this user is assigned to.
   * @param Role Either Admin or Student.
   * @param CountryId The country the user is from.
   * @param StateId Optional: The state they are in.
   * @param IsAda default to false, ADA accessablility.
   */
    public static Create({
        Country,
        Email,
        Firstname,
        Groups,
        IsAda,
        Lastname,
        Password,
        Role,
        SelectedPartner,
        State,
        StudentNumber,
        Username
    }: NewStudent): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<FullfilledApiResponse>('Create', {
            countryId: Country.Id,
            Email,
            Firstname,
            Groups,
            IsAda,
            Lastname,
            PartnerId: SelectedPartner.PartnerId,
            Password,
            Role,
            StateId: State.Id,
            StudentNumber,
            Username
        });

    }

    /**
	 * Checks to see if the username has already been taken by another user
	/**
	 * Returns true or false based on if the username is valid
	 * @param username A string of the user's username
	 */
    public static ValidateUsername(username: string) {

        return this.apiCaller.GET<boolean>('ValidateUsername', { username });

    }

    /**
   * Retrieve a student object by id.
   * @param id The unique id of the student.
   */
    public static Get(id: number): Promise<StudentWithDetails> {

        return this.apiCaller.GET<StudentWithDetails>('Get', { id });

    }

    /**
   * Retrieve a student's secret (account recovery) question and answer.
   * System administrators only — the backend returns null for any other role.
   * @param id The unique id of the student.
   */
    public static GetSecretQuestion(id: number): Promise<StudentSecretQuestion> {

        return this.apiCaller.GET<StudentSecretQuestion>('GetSecretQuestion', { id });

    }

    /**
   * Retrieve the list of PII claims that have been set for a student.
   * Requires the ViewPII permission on the server.
   * @param studentId The unique id of the student.
   */
    public static GetStudentClaims(studentId: number): Promise<RequiredClaim[]> {

        return this.apiCaller.GET<RequiredClaim[]>('GetStudentClaims', { studentId });

    }

    /**
   * Retrieve the student's course/exam pass-fail results, including a certificate link
   * where a certificate is available. Requires the ViewPII permission on the server.
   * @param studentId The unique id of the student.
   */
    public static GetStudentCourseResults(studentId: number): Promise<StudentCourseResult[]> {

        return this.apiCaller.GET<StudentCourseResult[]>('GetStudentCourseResults', { studentId });

    }

    /**
   * Update the student's PII claims. Each claim is matched by its claim type and its value
   * is replaced (an empty value clears the claim). Requires the EditPII permission on the server.
   * @param studentId The unique id of the student.
   * @param claims The PII claims to update.
   */
    public static UpdateStudentPiiClaims(studentId: number, claims: RequiredClaim[]): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<FullfilledApiResponse>('UpdateStudentPiiClaims', { claims, studentId });

    }

    /**
   * Retrieve a student object by username.
   * @param username The unique username of the student.
   */
    public static GetStudentByUsername(username: string): Promise<Student> {

        return this.apiCaller.GET<Student>('GetStudentByUsername', { username });

    }

    /**
   * Retrieve a student object by username in bulk.
   * @param username[] The unique username of the student.
   */
    public static GetBulkStudentsByUsername(usernames: string[]): Promise<{
    foundStudents: Student[];
    notFoundStudents: string[];
    userNameTakenStudents: string[];
  }> {

        return this.apiCaller.GET<{
      foundStudents: Student[];
      notFoundStudents: string[];
      userNameTakenStudents: string[];
    }>('GetBulkStudentsByUsername', { usernames });

    }

    /**
   * Retrieve a transcript for a student by id.
   * @param userId The unique id of the student.
   */
    public static GetAll(searchString = ''): Promise<Student[]> {

        return this.apiCaller.GET<Student[]>('GetAll', { searchString });

    }

    public static GetAllPaged(
        page = 1,
        pageSize = 25,
        sortField: StudentSortField | '' = '',
        sortDir = '',
        searchTerm = '',
        showArchived = false
    ): Promise<PaginatedResult<Student>> {

        return this.apiCaller.GET<PaginatedResult<Student>>('GetAllPaged', {
            page,
            pageSize,
            searchTerm,
            showArchived,
            sortDir,
            sortField
        });

    }

    /**
   * Retrieve a transcript for a student by id.
   * @param userId The unique id of the student.
   */
    public static GetTranscript(userId: number): Promise<Transcript> {

        return this.apiCaller.GET<Transcript>('GetTranscript', { userId });

    }

    /**
   * Builds a single-use bridge URL that signs the current system admin into the
   * student panel as the requested student. Open the returned URL in a new tab.
   * @param studentId The unique id of the student to log in as.
   * @param target Optional student-panel environment override. Defaults to `'current'`
   * (the current environment's student panel). Any explicit environment
   * (`'prod' | 'stage' | 'uat' | 'dev' | 'localhost'`) is a dev-only path that the
   * backend restricts to the Development feature ring (`Admin.Students.DevLoginAsStudent`).
   */
    public static GetStudentLoginBridgeUrl(studentId: number, target: StudentLoginTarget = 'current'): Promise<string> {

        return this.apiCaller.GET<string>('GetStudentLoginBridgeUrl', { studentId, target });

    }

    /**
   * Toggles Student Feature Flag access from 0 to 30 or vice versa.
   * @param id The unique id of the student.
   */
    public static ToggleStudentBetaRing(
        id: number,
        currentFeatureRingId: number
    ): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<FullfilledApiResponse>('ToggleStudentBetaRing', {
            id,
            currentFeatureRingId
        });

    }

    /**
   * Update a students details.
   * @param Id The unique id of the student.
   * @param Firstname The firstname of the student.
   * @param LastName The lastname of the student.
   * @param Email The email of the student.
   * @param IsAda If the student has any sort of disability.
   */
    public static Update({
        Country,
        Email,
        Firstname,
        Id,
        IsAda,
        Lastname,
        State,
        StudentNumber,
        Username
    }: StudentWithDetails): Promise<void> {

        return this.apiCaller.PUT<void>('Update', {
            CountryId: Country?.Id,
            Email,
            Firstname,
            Id,
            IsAda,
            Lastname,
            StateId: State?.Id,
            StudentNumber,
            Username: Username
        });

    }

    /**
   * Sets student to Archived
   * @param idList The unique id of the student in list form.
   */
    public static SetArchiveStudentsTestingCenter(
        idList: number[],
        archived: boolean
    ): Promise<FullfilledApiResponse> {

        return this.apiCaller.PUT<FullfilledApiResponse>(
            'SetArchiveStudentsTestingCenter',
            { archived, idList }
        );

    }

    public static GetSectionTestDetails(
        studentId: number,
        resultId: number,
        cultureId: number
    ): Promise<TestSectionDetail[]> {

        return this.apiCaller.GET<TestSectionDetail[]>('GetSectionTestDetails', {
            cultureId,
            resultId,
            studentId
        });

    }

    public static GetIdByUsername(username: string): Promise<number> {

        return this.apiCaller.GET<number>('GetIdByUsername', { username });

    }

    public static UpdateSectionTestDetails(
        userNotes: string,
        parentResultId: number,
        sectionTestResultId: number,
        sectionResultId: number,
        newSectionStartDate: string,
        newSectionLockoutDate: string,
        cascadeUpdate: boolean
    ): Promise<FullfilledApiResponse> {

        return this.apiCaller.PUT<FullfilledApiResponse>(
            'UpdateSectionTestDetails',
            {
                cascadeUpdate,
                newSectionLockoutDate:
          newSectionLockoutDate === '' || newSectionLockoutDate === undefined
              ? null
              : getFormattedDateApi(new Date(newSectionLockoutDate)),
                newSectionStartDate:
          newSectionStartDate === '' || newSectionStartDate === undefined
              ? null
              : getFormattedDateApi(new Date(newSectionStartDate)),
                parentResultId,
                sectionResultId,
                sectionTestResultId,
                userNotes
            }
        );

    }

    public static CreateNewStudentInfoField(
        fieldName: string,
        cultureId = 1
    ): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<FullfilledApiResponse>(
            'CreateNewStudentInfoField',
            {
                cultureId,
                fieldName
            }
        );

    }

    public static GetStudentInfoFieldsByPartner(
        cultureId = 1
    ): Promise<CustomFields> {

        return this.apiCaller.GET<CustomFields>('GetStudentInfoFieldsByPartner', {
            cultureId
        });

    }

    public static SetStudentInfoFieldValue(
        fieldName: string,
        fieldValue: string,
        studentId: number
    ): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<FullfilledApiResponse>(
            'SetStudentInfoFieldValue',
            {
                fieldName,
                fieldValue,
                studentId
            }
        );

    }

    /**
	 * Bulks resets passwords to a string that was given and then returns Success/Fail.
	/**
	 * Sends bulk Password Reset emails and returns either Success/Fail.
	 * @param userProfileIds An array of userProfile ids related to the users.
	 */
    public static BulkResetPassword(
        userProfileIds: number[],
        newPassword: string
    ): Promise<string> {

        return this.apiCaller.PUT<string>('BulkResetPassword', {
            newPassword,
            userProfileIds
        });

    }

    /**
   * Sends bulk Password Reset emails and returns either Success/Fail.
   * @param userProfileIds An array of userProfile ids related to the users.
   */
    public static BulkPasswordResetEmail(
        userProfileIds: number[]
    ): Promise<string> {

        return this.apiCaller.POST<string>('BulkPasswordResetEmail', {
            userProfileIds
        });

    }

    /**
   * Sends bulk Welcome emails and returns either Success/Fail.
   * @param userProfileIds An array of userProfile ids related to the users.
   */
    public static BulkWelcomeEmail(userProfileIds: number[]): Promise<string> {

        return this.apiCaller.POST<string>('BulkWelcomeEmail', { userProfileIds });

    }

    /**
   * Returns JSON array of generated signin links for selected users.
   * @param userProfileIds An array of userProfile ids related to the users.
   */
    public static BulkGenerateSigninLinks(
        userProfileIds: number[]
    ): Promise<string> {

        return this.apiCaller.POST<string>('BulkGenerateSigninLinks', {
            userProfileIds
        });

    }

    /**
   * Returns JSON array of generated signin links for selected users.
   * @param id The student id or group/access code id depending on context
   * @param context The calling page context (e.g., "student-certificates", "classroom-certificates", "access-code-certificates")
   */
    public static GetCertificateDetails(
        id: number,
        context: string
    ): Promise<Certificate[]> {

        return this.apiCaller.POST<Certificate[]>('GetCertificateDetails', {
            id,
            context
        });

    }

    /**
     * Get the details of a single student's redemption of an access code
     * (StudentContentAccess record).
     * @param studentAccessCodeId The StudentContentAccessId of the record to load.
     */
    public static GetStudentAccessDetail(
        studentAccessCodeId: number
    ): Promise<StudentContentAccessDetail> {

        return this.apiCaller.GET<StudentContentAccessDetail>('GetStudentAccessDetail', {
            studentAccessCodeId
        });

    }

    /**
     * Update a single student's redemption of an access code (StudentContentAccess record).
     * Only edits the student's redemption, not the underlying access code.
     * @param studentAccessCodeId The StudentContentAccessId of the record to update.
     * @param active Whether the redemption is active.
     * @param expirationDate The new expiration date (parseable date string).
     * @param testsRemaining Tests available, or null to leave unchanged.
     * @param booksRemaining Books available, or null to leave unchanged.
     * @param coursesRemaining Courses available, or null to leave unchanged.
     * @param cloudAccessRemaining Cloud access available, or null to leave unchanged.
     * @param productId The associated product to set, or null to leave unchanged.
     */
    public static UpdateStudentAccessDetail(
        studentAccessCodeId: number,
        active: boolean,
        expirationDate: string,
        testsRemaining: number | null,
        booksRemaining: number | null,
        coursesRemaining: number | null,
        cloudAccessRemaining: number | null,
        productId: number | null = null
    ): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<FullfilledApiResponse>('UpdateStudentAccessDetail', {
            active,
            booksRemaining,
            cloudAccessRemaining,
            coursesRemaining,
            expirationDate,
            productId,
            studentAccessCodeId,
            testsRemaining
        });

    }

    /**
     * Update only the expiration date of a student's content-access record for a NAJA
     * (category 55) course. The course category is validated server-side.
     * @param studentContentAccessId The StudentContentAccessId of the record to update.
     * @param expirationDate The new expiration date (parseable date string).
     * @param categoryId The course category id of the row being edited.
     */
    public static UpdateNajaCourseExpiration(
        studentContentAccessId: number,
        expirationDate: string,
        categoryId: number
    ): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<FullfilledApiResponse>('UpdateNajaCourseExpiration', {
            categoryId,
            expirationDate,
            studentContentAccessId
        });

    }

}
